import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { detectPanelColor, resizePoints } from '../utils/util';
import { Line } from 'react-konva';
import {
	DEV_STROKE_COLOR,
	FACET_HIGHLIGHT_COLOR,
	FACET_HIGHLIGHT_OFF_COLOR,
	KONVA_FACET_GROUP,
	PANEL_OFF_COLOR,
	PANEL_ON_COLOR,
	PANEL_OVERLAP_THRESHOLD,
	PANEL_STROKE_COLOR
} from '../../../constants';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { updateEnergyStateFromPanelClicks } from 'store/slices/EnergySlice/energySlice';
import { EnergyState } from 'store/slices/EnergySlice/types';
import { RootState } from 'store';
import { setIntermediateEnergyState, toggleOffsetState } from 'store/slices/OffsetModelSlice/offsetmodelSlice';
import { useRefs } from 'contexts/RefContext';
import { calculateOffsetPercentage, getDeratedValue, getPanelPxScale } from '../../../util';
import { setAddSelectedMovablePanel, setCurrentMovablePanels, setDeletedPanels } from 'store/slices/PanelSlice';
import { setActiveDragFunctionality, setMapOnlyFrozenPoint } from 'store/slices/ToolSlice';
import { KonvaEventObject } from 'konva/lib/Node';
import Konva from 'konva';
import { checkIntersection, getRotatedLinePoints, getSnappedPosition, isOverLap } from '../PanelUtil';
import { CurrentMovablePanel } from 'store/slices/ToolSlice/types';
import { getAbsolutePoints } from 'components/DisplayEnergy/util';
import { getExistingPanelsGroupID, getRoofSegmentIDFromGroupID, KonvaGroupNames } from '../utils';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { ORIENTATION } from 'store/slices/AddPanelModalSlice/types';

type Props = {
	panelData: RasterSolarPanel & { userMappedFacet: boolean },
	panelId: string,
	invalidAzimuth: boolean,
};

function SolarPanel(props: Props) {
	const { panelData: panel, panelId, invalidAzimuth } = props;
	const dispatch = useDispatch();
	// Every panel subscribes to the store, so each selector picks only the fields used here (compared with
	// shallowEqual). Selecting whole slices re-rendered every panel on any action, e.g. a zoom or pan update.
	const { energy, billEnergy, selectedPanels, deratedBy } = useSelector((state: RootState) => {
		const energyData = state.energyData.data as unknown as EnergyState;
		return {
			energy: energyData.energy,
			billEnergy: energyData.billEnergy,
			selectedPanels: energyData.selectedPanels,
			deratedBy: energyData.deratedBy,
		};
	}, shallowEqual);
	const userHasGivenPermToExceedOffset = useSelector((state: RootState) => state.offsetModal.data.userHasGivenPermToExceedOffset);
	const { editModeEnabled, roofIndexes, saveWithPanelColor, moveStage, rotateStage, enabledDevMode, threeDModelEnabled,
		hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint } = useSelector((state: RootState) => {
		const tool = state.toolNewPostions.data;
		return {
			editModeEnabled: tool.editModeEnabled,
			roofIndexes: tool.roofIndexes,
			saveWithPanelColor: tool.saveWithPanelColor,
			moveStage: tool.moveStage,
			rotateStage: tool.rotateStage,
			enabledDevMode: tool.enabledDevMode,
			threeDModelEnabled: tool.threeDModelEnabled,
			hideSatelliteOverlay: tool.hideSatelliteOverlay,
			activeDragFunctionality: tool.activeDragFunctionality,
			mapOnlyFrozenPoint: tool.mapOnlyFrozenPoint,
		};
	}, shallowEqual);
	const { enablePanelMove, deleteIndividualPanelMode, deleteArrayMode,
		currentMovablePanels, selectedMovablePanels } = useSelector((state: RootState) => {
		const panelData = state.panelSlice.data;
		return {
			enablePanelMove: panelData.enablePanelMove,
			deleteIndividualPanelMode: panelData.deleteIndividualPanelMode,
			deleteArrayMode: panelData.deleteArrayMode,
			currentMovablePanels: panelData.currentMovablePanels,
			selectedMovablePanels: panelData.selectedMovablePanels,
		};
	}, shallowEqual);
	const enablePanelDrag = enablePanelMove && !selectedMovablePanels.length;
	const { org, allRoofSegs, obstructions, groundMetersPerPixel } = useSelector((state: RootState) => {
		const roofData = state.roofData.data;
		return {
			org: roofData.org,
			allRoofSegs: roofData.allRoofSegs,
			obstructions: roofData.obstructions,
			groundMetersPerPixel: roofData.groundMetersPerPixel,
		};
	}, shallowEqual);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);
	const enableEditFacet = useSelector((state: RootState) => state.roofEditData.data.enableEditFacet);
	const shouldListen = !(moveStage || rotateStage || enableEditFacet);

	const canTogglePanel = allowedActions.includes('toggle-panels') || org.setting.isB2C;
	const obstructionPointsRef = useRef<Konva.Line[]>([]);

	const { sliderRef, konvaRef } = useRefs();
	const scale = useKonvaImageScale();

	const panelIsInEditMode = editModeEnabled || roofIndexes.includes(panel.segmentId);
	const canSelectPanel = !panelIsInEditMode && !moveStage && !rotateStage;

	const intersectedFacetRef = useRef<Konva.Line | null>(null);
	const panelRef = useRef<Konva.Line | null>(null);
	const isDraggingRef = useRef(false);
	const wasHiddenRef = useRef(hideSatelliteOverlay);

	const pxScale = getPanelPxScale(groundMetersPerPixel);

	const [initialPoints, setInitialPoints] = useState<number[]>([]);
	// const [color, setColor] = useState(() => {

	// 	if(selectedPanels.includes(panel.id)) {
	// 		if(saveWithPanelColor) return detectPanelColor(panel.yearlyEnergyDcKwh); 
	// 		return PANEL_ON_COLOR;
	// 	}
	// 	return PANEL_OFF_COLOR;
	// });
	const isPanelOn = selectedPanels.includes(panel.id);

	function updateOffsetModal() {
		let offsetUpdated = false;
		// previous condition color === PANEL_OFF_COLOR
		if (!isPanelOn) {
			const intermediiateOffset = calculateOffsetPercentage(getDeratedValue((energy + panel.yearlyEnergyDcKwh), deratedBy), billEnergy);
			dispatch(setIntermediateEnergyState({ value: { source: 'panel', data: { panelInfo: { panel, panelId } } } }));
			if (!userHasGivenPermToExceedOffset.offset120 && intermediiateOffset >= 120 && org.setting?.enableOffsetWarning) {
				dispatch(toggleOffsetState({ value: true }));
				offsetUpdated = true;
			}
			if (!userHasGivenPermToExceedOffset.offset150 && intermediiateOffset >= 150 && org.setting?.enableOffsetWarning) {
				dispatch(toggleOffsetState({ value: true }));
				offsetUpdated = true;
			}
		}
		return offsetUpdated;
	}

	function updateSLider(dir: string) {
		if (!sliderRef?.current) return;
		const step = +sliderRef.current.step;
		const sliderVal = +sliderRef.current.value;
		let rangeVl;
		if (dir === 'up') {
			sliderRef.current.value = (sliderVal + step).toString();
			rangeVl = ((sliderVal + step) / +sliderRef.current.max) * 100;
		}
		else {
			sliderRef.current.value = (sliderVal - step).toString();
			rangeVl = ((sliderVal - step) / +sliderRef.current.max) * 100;
		}
		sliderRef.current.style.background = `linear-gradient(to right, var(--secondary) 0%, var(--secondary) ${rangeVl}%, #c4c4c4 ${rangeVl}%, #c4c4c4 100%)`;
	}

	function updateColorAndMetada() {
		const energyBoost = !isPanelOn ? panel.yearlyEnergyDcKwh : -panel.yearlyEnergyDcKwh;
		const irradianceBoost = !isPanelOn ? panel.irradiance : -panel.irradiance;
		dispatch(updateEnergyStateFromPanelClicks({ energyBoost, panelId, irradianceBoost }));
		// setColor((prevState) => (prevState === PANEL_OFF_COLOR ? detectPanelColor(panel.yearlyEnergyDcKwh) : PANEL_OFF_COLOR));
	}

	function handleClick() {
		if (enablePanelMove) {
			dispatch(setAddSelectedMovablePanel(panel));
			return;
		}
		if (deleteArrayMode) {
			const panelGroup = konvaRef?.current?.findOne(`#${getExistingPanelsGroupID(panel.segmentId)}`) as Konva.Group;
			if (panelGroup) {
				const allPanels = (panelGroup.children || []) as Konva.Line[];
				const deletedPanelIds = allPanels.map(p => ({
					panelId: p.id(),
					roofSegmentId: panel.segmentId
				}));
				dispatch(setDeletedPanels({ data: deletedPanelIds }));
			}
			return;
		}
		if (!canTogglePanel) return;
		if (deleteIndividualPanelMode) {
			dispatch(setDeletedPanels({ data: [{ panelId, roofSegmentId: panel.segmentId }] }));
			return;
		}
		if (panelIsInEditMode) return;
		if (!canSelectPanel) return;
		if (!updateOffsetModal()) {
			updateColorAndMetada();
			// updateSLider((color === PANEL_OFF_COLOR) ? 'up' : 'down');
			updateSLider((!isPanelOn) ? 'up' : 'down');
		}
	}

	const panelPoints = useMemo(() => {
		const doesPanelExist = currentMovablePanels.find(eachPanel => eachPanel.panelId === panel.id);
		let points = panel.exteriorCoords;
		if (doesPanelExist) {
			points = doesPanelExist.exteriorCoords;
		}
		return resizePoints(points.flat(), scale);
	}, [scale, panel.exteriorCoords, currentMovablePanels]);

	useEffect(() => {
		if (!editModeEnabled) return;
		if (!enablePanelMove && panelRef?.current && konvaRef?.current) {
			if (!panelRef.current?.attrs?.customPosition && !panelRef.current.attrs.isMovablePanel) return;

			const panelParentID = panelRef.current?.getParent()?.id();
			if (panelParentID && (getRoofSegmentIDFromGroupID(panelParentID) !== panel.segmentId)) {
				const originPanelGroup = konvaRef?.current?.findOne(`#${getExistingPanelsGroupID(panel.segmentId)}`);
				panelRef.current.moveTo(originPanelGroup);
			}
			panelRef.current.setPosition({ x: 0, y: 0 });
			panelRef.current.attrs.customPosition = undefined;
			panelRef.current.attrs.isMovablePanel = false;
			panelRef.current.points(resizePoints(panel.exteriorCoords.flat(), scale));
		}
	}, [enablePanelMove, panelRef, panel, konvaRef, scale, editModeEnabled, currentMovablePanels]);

	// allow newly updated panel to move into new group
	useEffect(() => {
		if (panelRef.current) {
			const panelId = panel.id;
			const exist = currentMovablePanels.find(eachPanel => eachPanel.panelId === panelId);

			if (exist && konvaRef?.current) {
				const newPanelGroup = konvaRef.current.findOne(`#${getExistingPanelsGroupID(exist.roofSegmentId)}`);
				if (newPanelGroup) {
					panelRef.current.attrs.isMovablePanel = true;
					panelRef.current.moveTo(newPanelGroup);
				}
			}
		}
	}, [currentMovablePanels]);

	const strokeColor = useMemo(() => {
		if (enablePanelMove && currentMovablePanels.find(eachPanel => eachPanel.panelId === panelId)) {
			return FACET_HIGHLIGHT_COLOR;
		}
		if (invalidAzimuth) {
			return 'red';
		}
		if (enabledDevMode && panel.userMapped) {
			return DEV_STROKE_COLOR;
		}
		return PANEL_STROKE_COLOR;
		// return ((panel?.userMapped || panel.userMappedFacet) && editModeEnabled) ? STROKE_COLOR_FOR_USER_MAPPED_PANEL : PANEL_STROKE_COLOR;
	}, [
		invalidAzimuth,
		enabledDevMode,
		editModeEnabled,
		panel.userMapped,
		panel.userMappedFacet,
		currentMovablePanels,
		enablePanelMove
	]);

	useEffect(() => {
		if (!konvaRef?.current) return;
	
		const stage = konvaRef.current;
	
		const allObsShape = obstructions
			.map((eachObs) => stage.findOne(`#${eachObs.id}`))
			.filter((node): node is Konva.Line => node instanceof Konva.Line); // circle obstructions have no .points()
	
		obstructionPointsRef.current = allObsShape;
	
	}, [obstructions, scale.x, scale.y]);

	const handlePointerDown = (e: any) => {
		const shape = e.target;
		if (panelRef.current)
			panelRef.current.attrs.customPosition = shape.position();
		setInitialPoints(shape.points());

	};

	const DRAG_FUNCTIONALITY = 'movePanel';

	// When map only turns on mid-drag, freeze this panel: snapshot its exact position into
	// the global store (handleDragMove below early-returns while hidden, so geometry/position
	// simply stop updating — nothing else to pin here). On exit, restore the snapshotted
	// value — the panel sits at its last frozen position until the user moves the mouse, at
	// which point it follows again naturally.
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDraggingRef.current || activeDragFunctionality !== DRAG_FUNCTIONALITY) return;
		const shape = panelRef.current;
		if (!shape) return;

		if (hideSatelliteOverlay && !wasHidden) {
			dispatch(setMapOnlyFrozenPoint(shape.getAbsolutePosition()));
			return;
		}

		if (!hideSatelliteOverlay && wasHidden) {
			if (mapOnlyFrozenPoint) shape.setAbsolutePosition(mapOnlyFrozenPoint);
			dispatch(setMapOnlyFrozenPoint(null));
			shape.getLayer()?.batchDraw();
		}
	}, [hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint, dispatch]);

	const handleDragStart = (e: KonvaEventObject<DragEvent>) => {
		const shape = e.target as Konva.Line;
		shape.fill(PANEL_ON_COLOR);
		isDraggingRef.current = true;
		dispatch(setActiveDragFunctionality(DRAG_FUNCTIONALITY));
	};

	const handleDragMove = useCallback(function (e: KonvaEventObject<DragEvent>) {
		if (hideSatelliteOverlay) return;
		if (!konvaRef?.current) return;
		const shape = e.target as Konva.Line;
		konvaRef.current.container().style.cursor = 'move';
		const currentPos = shape.position();
		intersectedFacetRef.current = null;

		const allFacets = konvaRef.current.find(`.${KonvaGroupNames.existingFacet}`) as Konva.Line[];
		const intersectedFacet = checkIntersection(allFacets, shape, false);
		if (!intersectedFacet) return;

		intersectedFacetRef.current = intersectedFacet as Konva.Line;
		const parentGroup = intersectedFacetRef.current?.parent as Konva.Group | null;
		if (!parentGroup) return;
		const panelGroup = parentGroup?.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group | null;
		if (!panelGroup) return;
		shape.moveTo(panelGroup);

		const siblings = panelGroup?.children?.filter(c =>
			c instanceof Konva.Line &&
			c.name() &&
			c.name().startsWith('panel-') &&
			c.id() !== shape.id()
		) as Konva.Line[];
		const matchingRoofSeg = allRoofSegs.find(seg => seg.id === intersectedFacet.id());
		if (matchingRoofSeg) {
			let panelWidth = matchingRoofSeg.panelWidth;
			let panelHeight = matchingRoofSeg.panelHeight;

			if (panel.orientation === ORIENTATION.LANDSCAPE && panelHeight > panelWidth) {
				[panelWidth, panelHeight] = [panelHeight, panelWidth];
			}
			if (panel.orientation === 'POTRAITT' && panelWidth > panelHeight) {
				[panelWidth, panelHeight] = [panelHeight, panelWidth];
			}

			const rotationAngle = (matchingRoofSeg.azimuthDegrees) % 360;

			const dims = {
				width: panelWidth / pxScale * scale.x,
				height: panelHeight / pxScale * Math.cos(matchingRoofSeg.pitchDegrees * (Math.PI / 180)) * scale.y
			};
			const points = getRotatedLinePoints(e.target.getRelativePointerPosition()!, dims.width, dims.height, rotationAngle);
			shape.points(points);

			if (!siblings?.length) return;
			const { hasOverlap } = isOverLap(shape, siblings as Konva.Line[], PANEL_OVERLAP_THRESHOLD);
			const doesObsTouched = checkIntersection(obstructionPointsRef.current, shape, false);

			const newStrokeColor = hasOverlap ? 'red' : strokeColor;
			shape.stroke(newStrokeColor);
			// const panelColor = doesObsTouched ? INVALID_PANEL_ON_COLOR : SEMI_TRANPARENT_BLACK_PANEL;
			// shape.fill(panelColor);

			const snappedPosition = getSnappedPosition(currentPos, shape, siblings);
			if (snappedPosition) shape.position(snappedPosition);
		}
	}, [konvaRef, allRoofSegs, scale.x, scale.y, panel.orientation, strokeColor, pxScale, obstructions, hideSatelliteOverlay]);

	function handleDragEnd(e: KonvaEventObject<DragEvent>) {
		isDraggingRef.current = false;
		dispatch(setActiveDragFunctionality(null));
		dispatch(setMapOnlyFrozenPoint(null));
		if (!konvaRef?.current) return;
		const shape = e.target as Konva.Line;
		konvaRef.current.container().style.cursor = 'default';
		if (!panelRef?.current) return;
		//dont allow if overlap deteected/ or not facet detected move it back to original pos
		if (!intersectedFacetRef?.current || shape.attrs.stroke === 'red') {
			shape.fill(panelColor);
			shape.points(initialPoints);
			shape.stroke(strokeColor);
			panelRef.current.attrs.customPosition && shape.position(panelRef.current.attrs.customPosition);
			const prevousSegmentID = shape.attrs.prevousSegmentID ?? panel.segmentId;
			const previousGroup = konvaRef.current.findOne(`#${prevousSegmentID}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KonvaGroupNames.existingPanelsGroup}`);
			if (previousGroup && previousGroup.id() !== shape.parent?.id()) {
				shape.moveTo(previousGroup);
			}
			return;
		}
		intersectedFacetRef.current.fill(FACET_HIGHLIGHT_OFF_COLOR);

		const rs = allRoofSegs.find(r => r.id === intersectedFacetRef.current?.id());
		if (!rs) return;
		shape.attrs.prevousSegmentID = rs.id;
		const exteriorCoords: number[][] = [];
		const coords = getAbsolutePoints(shape, scale, konvaRef.current);
		for (let i = 0; i < coords.length; i += 2) {
			exteriorCoords.push([coords[i], coords[i + 1]]);
		}
		const shouldClosePolygon = exteriorCoords.length === 4; // since panels are rect;
		if (shouldClosePolygon) exteriorCoords.push(exteriorCoords[0]);
		shape.points(resizePoints(exteriorCoords.flat(), scale));
		shape.position({ x: 0, y: 0 });
		const movedPanelCongif: CurrentMovablePanel = {
			orientation: panel.orientation,
			originSegmentId: panel.segmentId,
			exteriorCoords,
			panelId: shape.id(),
			roofSegmentId: rs.id,
		};

		dispatch(setCurrentMovablePanels({ currentMovablePanelArr: [movedPanelCongif], updateHistory: true }));
	}

	const panelVisibility = useMemo(() => {
		const panelNode = panelRef.current;
		if (!panelNode) return true;

		// const isPanelOff = color === PANEL_OFF_COLOR;
		const hideIn3DView = threeDModelEnabled && !(isPanelOn || panelIsInEditMode);

		return !hideIn3DView;
	}, [isPanelOn, panelIsInEditMode, threeDModelEnabled]);

	const panelColor = useMemo(() => {
		// if(userHasClickedSave && !saveWithPanelColor) {
		// 	return isPanelOn ? SEMI_TRANPARENT_BLACK_PANEL : PANEL_OFF_COLOR;
		// }
		if(isPanelOn || panelIsInEditMode) {
			return saveWithPanelColor ? detectPanelColor(panel.yearlyEnergyDcKwh) : PANEL_ON_COLOR;
		}

		return PANEL_OFF_COLOR;
	}, [
		isPanelOn, panel?.obstructs, 
		panelIsInEditMode, saveWithPanelColor, 
		panel.yearlyEnergyDcKwh, currentMovablePanels
	]);

	return (
		<Line
			ref={panelRef}
			id={panel.id}
			stroke={strokeColor}
			closed={true}
			strokeWidth={1}
			strokeScaleEnabled={false}
			points={panelPoints}
			fill={panelColor}
			onClick={handleClick}
			onTap={handleClick}
			draggable={enablePanelDrag}
			dragDistance={5}
			onPointerDown={handlePointerDown}
			onDragStart={handleDragStart}
			onDragMove={handleDragMove}
			onDragEnd={handleDragEnd}
			listening={shouldListen}
			name={`panel-${panel.segmentId}`}
			visible={panelVisibility}
			obstruction={panel?.obstructs}
		/>
	);
}

export default memo(SolarPanel);